import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useGameStore } from '@/store/gameStore'
import { usePmWalletStore } from '@/store/pmWalletStore'
import { hapticSuccess, hapticError } from '@/telegram'
import { toast } from 'sonner'
import { useClobClient } from './useClobClient'
import { placeOrder } from '@/lib/clobClient'
import type { Position } from '@/lib/polymarketApi'

interface PlaceBetParams {
  tokenId: string
  side: 'YES' | 'NO'
  amountUsd: number
  tickSize?: string
  negRisk?: boolean
  yesPrice?: number
  noPrice?: number
  useExactPrice?: boolean
}

export function usePlaceBet() {
  const queryClient = useQueryClient()
  const { incrementStreak, resetStreak, setLastBetTimestamp, triggerConfetti } = useGameStore()
  const clobClient = useClobClient()
  const activeWallet = usePmWalletStore((s) => s.activeWallet)
  const pmProxyAddress = usePmWalletStore((s) => s.pmProxyAddress)
  const activeAddress = activeWallet === 'pm' ? pmProxyAddress : null

  return useMutation({
    mutationFn: async (params: PlaceBetParams) => {
      if (clobClient) {
        return placeOrder(clobClient, {
          tokenId: params.tokenId,
          side: params.side,
          amountUsd: params.amountUsd,
          tickSize: params.tickSize ?? '0.001',
          negRisk: params.negRisk ?? false,
          yesPrice: params.yesPrice,
          noPrice: params.noPrice,
          useExactPrice: params.useExactPrice,
        })
      }
      throw new Error('Connect wallet to place bets')
    },
    onSuccess: (_data, params) => {
      const price = params.side === 'YES' ? (params.yesPrice ?? 0.5) : (params.noPrice ?? 0.5)
      const addedSize = price > 0 ? params.amountUsd / price : 0
      const address = activeAddress ?? ''
      if (address && addedSize > 0) {
        const list = queryClient.getQueryData<Position[]>(['positions', address])
        if (Array.isArray(list)) {
          const existing = list.find((p) => p.asset === params.tokenId)
          const newList = existing
            ? list.map((p) =>
                p.asset === params.tokenId
                  ? {
                      ...p,
                      size: p.size + addedSize,
                      avgPrice: (p.size * p.avgPrice + addedSize * price) / (p.size + addedSize),
                    }
                  : p
              )
            : [...list, { asset: params.tokenId, size: addedSize, avgPrice: price }]
          queryClient.setQueryData(['positions', address], newList)
        }
      }
      void queryClient.invalidateQueries({ queryKey: ['openOrders'] })
      void queryClient.invalidateQueries({ queryKey: ['positions'] })
      hapticSuccess()
      incrementStreak()
      setLastBetTimestamp(Date.now())
      triggerConfetti()
      toast.success('Bet placed! 🎯')
    },
    onError: (err) => {
      hapticError()
      resetStreak()
      toast.error(err instanceof Error ? err.message : 'Bet failed')
    },
  })
}
